import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import type { GroupRestaurant } from '../api/restaurantGroups';
import { groupReserveUrl } from '../utils/groupReserveUrl';
import {
  resolveRestaurantText,
  RESTAURANT_GROUP_BUTTON_TEXT,
  RESTAURANT_GROUP_DESCRIPTION,
} from '../utils/restaurantText';

export interface GroupRestaurantCardProps {
  restaurant: GroupRestaurant;
  buttonBg?: string;
  buttonText?: string;
}

export function GroupRestaurantCard({ restaurant, buttonBg, buttonText }: GroupRestaurantCardProps) {
  const { t, i18n } = useTranslation('booking');
  const lang = i18n.language;

  const hasImage = Boolean(restaurant.customStyle?.bodyImage);
  const reserveUrl = groupReserveUrl(restaurant);

  const customButtonLabel = resolveRestaurantText(restaurant.appTexts, lang, RESTAURANT_GROUP_BUTTON_TEXT, '');
  const buttonLabel = customButtonLabel || t('GROUP_RESERVE');
  const hasCustomButton = Boolean(customButtonLabel);

  const description = resolveRestaurantText(restaurant.appTexts, lang, RESTAURANT_GROUP_DESCRIPTION, '');
  const hasDescription = Boolean(description);

  const pillStyle: React.CSSProperties | undefined =
    buttonBg || buttonText
      ? {
          ...(buttonBg ? { backgroundColor: buttonBg } : {}),
          ...(buttonText ? { color: buttonText } : {}),
        }
      : undefined;

  const hasAddress = Boolean(restaurant.address);

  return (
    <article
      data-testid="group-restaurant-card"
      data-verify-unit="GroupRestaurantCard"
      data-verify-has-image={String(hasImage)}
      data-verify-has-address={String(hasAddress)}
      data-verify-has-description={String(hasDescription)}
      data-verify-has-custom-button={String(hasCustomButton)}
      data-verify-subdomain={restaurant.subdomainPrefix}
      className="bg-card rounded-2xl shadow-sm border border-border/60 flex flex-col overflow-hidden"
    >
      <div className="relative w-full aspect-[3/2] overflow-hidden">
        {hasImage ? (
          <img
            src={restaurant.customStyle!.bodyImage as string}
            alt={restaurant.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            aria-hidden="true"
            className="w-full h-full bg-gradient-to-br from-brand-muted to-brand-light"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-6">
        <h2 className="text-xl font-display text-foreground leading-tight">
          {restaurant.name}
        </h2>

        {hasDescription && (
          <p className="text-sm text-foreground/70" data-testid="group-restaurant-description">
            {description}
          </p>
        )}

        {hasAddress && (
          <>
            <hr className="border-t border-border/40" />
            <div className="text-center" data-testid="group-restaurant-address">
              <p className="text-sm text-foreground/60">{restaurant.address}</p>
              {restaurant.address2 && (
                <p className="text-sm text-foreground/60">{restaurant.address2}</p>
              )}
            </div>
          </>
        )}

        <Button
          asChild
          className="mt-auto w-full py-6 text-lg gap-2 bg-brand hover:bg-brand-dark text-brand-foreground rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
        >
          <a
            href={reserveUrl}
            style={pillStyle}
          >
            <Calendar size={20} aria-hidden="true" />
            {buttonLabel}
          </a>
        </Button>
      </div>
    </article>
  );
}

export default GroupRestaurantCard;
